// Ink on the canvas: the sales manager's marks, kept in the page's own units — hundredths of a percent of the plan or
// map — so a circle stays on the room at any zoom and on any screen. Each page keeps its own ink: a plan's two units,
// the location map and the master plan are different pages. The navigator's book is the source of truth; every other
// screen draws what it is sent, and only from the host or someone he has given a pen (context/21-canvas-whiteboard.md).
import { roleOf } from "./sync.js";
import { isBoard, isStill } from "./render.js";
import { cleanStroke, LIMITS, TOOLS, UNITS } from "../../../lib/strokes.js";

// The rules for a mark live in lib/strokes.js, shared with the studio's store (W4).
export { cleanStroke, LIMITS, TOOLS };
export const INK_KINDS = new Set(["plan", "fit", "board", "still"]);

// The page a view is on, or null where there is no ink (the film, the 360°, the price page, …).
export function pageKey(scene, view = {}) {
  if (!scene || !INK_KINDS.has(scene.kind)) return null;
  if (scene.kind === "plan") {
    const variant = scene.variants && scene.variants[view.variant] ? view.variant : Object.keys(scene.variants || {})[0];
    return variant ? `${scene.id}:${variant}` : null;
  }
  return scene.id;
}

export function pageSize(scene, view = {}) {
  if (!scene) return null;
  const v = scene.kind === "plan" ? (scene.variants[view.variant] || Object.values(scene.variants)[0]) : scene;
  return v && +v.w > 0 && +v.h > 0 ? { w: +v.w, h: +v.h } : null;
}

// Screen percentages → wire units, and back.
export const toUnits = (pct) => Math.max(0, Math.min(UNITS, Math.round(pct * 100)));
export const toPct = (u) => u / 100;

// A stroke as it may be trusted: cleanStroke, from lib/strokes.js (shared with the studio's store).

// Who may draw (W2): the host always; anyone else only while the host's latest pen message names them. Checked on
// every screen against the identity LiveKit stamps, never an attribute; a blank identity (the hidden listener) never.
const NOBODY = new Set();
export function mayDraw(participant, holders = NOBODY) {
  const id = participant && participant.identity;
  if (!id) return false;
  return roleOf(participant) === "host" || holders.has(id);
}
// What a delete from this sender may take: the host anything; a pen holder only her own strokes.
export function allowedDeletes(strokes, ids, sender, isHost) {
  const asked = new Set((Array.isArray(ids) ? ids : []).filter((i) => typeof i === "string").slice(0, LIMITS.strokes));
  return strokes.filter((s) => asked.has(s.id) && (isHost || s.by === sender)).map((s) => s.id);
}

// One colour per person, the same on every screen: the host gold, the customer blue, each family member their own.
const GUEST_COLOURS = ["#8E3B6E", "#2F7D4F", "#B4541A", "#5B4BB7", "#2C7A8C", "#9C2F2F"];
export function colourOf(identity) {
  const role = roleOf({ identity });
  if (role === "host") return "#9A7120";
  if (role === "customer") return "#1F63A8";
  let h = 0;
  for (const c of String(identity || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return GUEST_COLOURS[h % GUEST_COLOURS.length];
}
function hlOf(colour) {
  if (colour === "#9A7120") return "rgba(222, 190, 110, .55)";
  const n = parseInt(colour.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, .26)`;
}

export const inkId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)).replace(/-/g, "").slice(0, 20);

// Every page's ink, with undo. The navigator's copy is the truth; a customer's copy is replaced by his snapshot.
export class InkBook {
  constructor() { this.pages = new Map(); }
  get(page) { return this.pages.get(page) || []; }
  add(page, stroke) {
    const s = cleanStroke(stroke);
    if (!page || !s || s.tool === "laser") return null;
    if (!this.pages.has(page) && this.pages.size >= LIMITS.pages) return null;
    const list = this.pages.get(page) || [];
    const at = list.findIndex((x) => x.id === s.id);
    // The same stroke again replaces it — only from the person who drew it. An id is visible to every screen, so a
    // replace from anyone else would let a pen holder overwrite (then delete) someone else's mark (audit W1–W3, P0).
    const points = list.reduce((n, x) => n + x.pts.length, 0);
    if (at >= 0) {
      // A stroke sent again replaces itself — only from the person who drew it, and within the page's points (re-audit N1).
      if (list[at].by !== s.by || points - list[at].pts.length + s.pts.length > LIMITS.pagePoints) return null;
      list[at] = s;
      return s;
    }
    if (list.length >= LIMITS.strokes) return null;
    // A stroke lives on one page: the same id on another page is someone reusing a mark they saw (W4 audit P2). The
    // glass is the one move there is — Freeze carries its marks onto the still, and phones hear the still before the
    // glass is cleared. And a page holds so many points in all, so it can always be kept (300 long strokes: megabytes).
    for (const [other, l] of this.pages) if (other !== page && other !== "share" && l.some((x) => x.id === s.id)) return null;
    if (points + s.pts.length > LIMITS.pagePoints) return null;
    list.push(s);
    this.pages.set(page, list);
    return s;
  }
  remove(page, ids) {
    const list = this.pages.get(page);
    if (!list) return [];
    const gone = list.filter((s) => ids.includes(s.id));
    this.pages.set(page, list.filter((s) => !ids.includes(s.id)));
    return gone;
  }
  // The last stroke this person made on this page.
  lastBy(page, by) {
    const list = this.get(page);
    for (let i = list.length - 1; i >= 0; i--) if (list[i].by === by) return list[i];
    return null;
  }
  clear(page) {
    const gone = this.get(page);
    this.pages.delete(page);
    return gone;
  }
  snapshot() {
    return Object.fromEntries([...this.pages].filter(([, l]) => l.length));
  }
  load(snap) {
    this.pages = new Map();
    if (!snap || typeof snap !== "object") return this;
    for (const [page, list] of Object.entries(snap).slice(0, 200)) {
      if (typeof page !== "string" || page.length > 80 || !Array.isArray(list)) continue;
      for (const s of list.slice(0, LIMITS.strokes)) this.add(page, s);
    }
    return this;
  }
  count(page) { return this.get(page).length; }
  // One person's marks made to match the host's record of them, everyone else's untouched: after a pen is taken back,
  // a stroke of hers that reached a phone but not the host goes, and nobody else's in-flight stroke is lost (re-audit N2).
  syncOwner(owner, pages) {
    const want = new Map();
    for (const [page, list] of Object.entries(pages || {})) if (Array.isArray(list)) want.set(page, list);
    for (const [page, list] of this.pages) {
      const keep = new Set((want.get(page) || []).map((s) => s && s.id));
      this.pages.set(page, list.filter((s) => s.by !== owner || keep.has(s.id)));
    }
    for (const [page, list] of want) for (const s of list.slice(0, LIMITS.strokes)) if (s && s.by === owner) this.add(page, s);
    return this;
  }
  // The pages holding one person's marks, and just those marks.
  ownedBy(owner) {
    const out = {};
    for (const [page, list] of this.pages) { const mine = list.filter((s) => s.by === owner); if (mine.length) out[page] = mine; }
    return out;
  }
}

// The nearest stroke to a point (in units) within reach — what the eraser takes. Measured to the stroke's segments,
// not just its points: a quick straight line has only two.
export function nearestStroke(strokes, pt, reach = 350) {
  let best = null, bd = reach;
  for (const s of strokes) {
    const body = s.tool === "label" ? 300 : 0;
    const P = s.pts;
    let d = P.length === 1 ? Math.hypot(P[0][0] - pt[0], P[0][1] - pt[1]) : Infinity;
    for (let i = 1; i < P.length; i++) d = Math.min(d, toSegment(pt, P[i - 1], P[i]));
    if (d - body < bd) { bd = d - body; best = s; }
  }
  return best;
}
function toSegment(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len = dx * dx + dy * dy;
  const t = len ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len)) : 0;
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

// ------------------------------------------------------------------------------------------ drawing --
const f1 = (v) => Math.round(v * 10) / 10;

// A smooth path through the points: quadratic curves through the midpoints.
export function smoothPath(P) {
  if (!P.length) return "";
  if (P.length === 1) return `M${f1(P[0][0])} ${f1(P[0][1])}l0.1 0`;
  let d = `M${f1(P[0][0])} ${f1(P[0][1])}`;
  for (let i = 1; i < P.length - 1; i++) {
    const [x1, y1] = P[i], [x2, y2] = P[i + 1];
    d += ` Q${f1(x1)} ${f1(y1)} ${f1((x1 + x2) / 2)} ${f1((y1 + y2) / 2)}`;
  }
  const e = P[P.length - 1];
  return `${d} L${f1(e[0])} ${f1(e[1])}`;
}

// One stroke as SVG, in the page's own pixel units (the ink layer's viewBox is the page's w × h).
export function strokeSVG(s, w, h, { fading = false } = {}) {
  const k = Math.max(w, h) / 1000;
  const pen = colourOf(s.by || "host-");
  const c = { pen, hl: hlOf(pen) };
  const P = s.pts.map(([x, y]) => [(x / UNITS) * w, (y / UNITS) * h]);
  const cap = 'stroke-linecap="round" stroke-linejoin="round" fill="none"';
  if (s.tool === "arrow") {
    const a = P[0], b = P[P.length - 1];
    if (!b) return "";
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]), hl = 26 * k;
    const h1 = [b[0] - hl * Math.cos(ang - 0.45), b[1] - hl * Math.sin(ang - 0.45)];
    const h2 = [b[0] - hl * Math.cos(ang + 0.45), b[1] - hl * Math.sin(ang + 0.45)];
    return `<path d="M${f1(a[0])} ${f1(a[1])}L${f1(b[0])} ${f1(b[1])}M${f1(h1[0])} ${f1(h1[1])}L${f1(b[0])} ${f1(b[1])}L${f1(h2[0])} ${f1(h2[1])}" stroke="${c.pen}" stroke-width="${f1(6.5 * k)}" ${cap}/>`;
  }
  const d = smoothPath(P);
  if (s.tool === "laser") {
    return `<g class="laser${fading ? " fade" : ""}"><path d="${d}" stroke="rgba(233, 185, 73, .35)" stroke-width="${f1(18 * k)}" ${cap}/><path d="${d}" stroke="#F2C75C" stroke-width="${f1(5 * k)}" ${cap}/></g>`;
  }
  // A highlighter is a wide translucent stroke. No blend mode: inside a transformed layer Chrome paints it black.
  if (s.tool === "hl") return `<path d="${d}" stroke="${c.hl}" stroke-width="${f1(30 * k)}" ${cap}/>`;
  return `<path d="${d}" stroke="${c.pen}" stroke-width="${f1(6.5 * k)}" ${cap}/>`;
}

// Paints a page's ink, the strokes being drawn and fading lasers into a canvas root. Labels are set as text, never HTML.
export function paintInk(root, strokes, live = []) {
  const svg = root.querySelector(".ink");
  const lbls = root.querySelector(".ink-lbls");
  if (!svg || !lbls) return;
  const [, , w, h] = (svg.getAttribute("viewBox") || "0 0 0 0").split(/\s+/).map(Number);
  if (!w || !h) return;
  svg.innerHTML = strokes.filter((s) => s.tool !== "label").map((s) => strokeSVG(s, w, h)).join("")
    + live.filter((s) => s.tool !== "label").map((s) => strokeSVG(s, w, h, { fading: s.fading })).join("");
  const chips = strokes.filter((s) => s.tool === "label").map((s) => {
    const el = document.createElement("span");
    el.className = "ink-lbl";
    el.style.color = el.style.borderColor = colourOf(s.by || "host-");
    el.style.left = `${toPct(s.pts[0][0])}%`;
    el.style.top = `${toPct(s.pts[0][1])}%`;
    el.textContent = s.text;
    return el;
  });
  lbls.replaceChildren(...chips);
}

// Where a pointer event lands on the page, in units — or null off the page (clamped while a stroke is being drawn).
export function unitsAt(fitEl, clientX, clientY, clamp = false) {
  if (!fitEl) return null;
  const r = fitEl.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  let x = ((clientX - r.left) / r.width) * 100, y = ((clientY - r.top) / r.height) * 100;
  if (clamp) { x = Math.max(0, Math.min(100, x)); y = Math.max(0, Math.min(100, y)); }
  else if (x < 0 || x > 100 || y < 0 || y > 100) return null;
  return [toUnits(x), toUnits(y)];
}

// Other people's pointers on this page: a dot and a name each, in the page's own units (percent), kept by identity.
export function paintPeers(root, peers) {
  const fit = root.querySelector(".cv-fit");
  if (!fit) return;
  const seen = new Set();
  for (const p of peers) {
    seen.add(p.id);
    let el = fit.querySelector(`.ptr-peer[data-id="${CSS.escape(p.id)}"]`);
    if (!el) {
      el = document.createElement("div");
      el.className = "ptr-peer";
      el.dataset.id = p.id;
      const tag = document.createElement("span");
      el.append(tag);
      fit.append(el);
    }
    el.style.left = `${p.x}%`;
    el.style.top = `${p.y}%`;
    el.style.setProperty("--peer", p.colour);
    el.classList.toggle("gone", Boolean(p.gone));
    el.firstChild.textContent = p.name;
  }
  for (const el of fit.querySelectorAll(".ptr-peer")) if (!seen.has(el.dataset.id)) el.remove();
}

// The canvas's own name for the place under a point (its spots, from Mana's material), if one is close.
export function spotNear(spots, x, y, within = 14) {
  let best = null, bd = within;
  for (const s of spots || []) {
    const d = Math.hypot(s.x - x, s.y - y);
    if (d < bd) { bd = d; best = s; }
  }
  return best ? String(best.label || "").split(" · ")[0] : null;
}

// Other people's pointers on the price page, each at its place in a row (docSpot), a dot and a name.
export function paintDocPeers(root, peers, spotOf) {
  const doc = root.querySelector(".cv-doc");
  if (!doc) return;
  const seen = new Set();
  for (const p of peers) {
    const at = spotOf(doc, p);
    if (!at) continue;
    seen.add(p.id);
    let el = doc.querySelector(`.ptr-peer[data-id="${CSS.escape(p.id)}"]`);
    if (!el) { el = document.createElement("div"); el.className = "ptr-peer"; el.dataset.id = p.id; el.append(document.createElement("span")); doc.append(el); }
    el.style.left = `${at.left}px`; el.style.top = `${at.top}px`;
    el.style.setProperty("--peer", p.colour);
    el.classList.toggle("gone", Boolean(p.gone));
    el.firstChild.textContent = p.name;
  }
  for (const el of doc.querySelectorAll(".ptr-peer")) if (!seen.has(el.dataset.id)) el.remove();
}

// A pointer message (cvq), in one of its three shapes, as it may be trusted: a point on a page (percent), a direction
// in a 360° viewpoint (u, v and the frame), or a place in a row of the price page. Anything else is nobody pointing.
export function peerFrom(body, participant, name) {
  if (!body || typeof body.page !== "string" || !participant?.identity) return null;
  const base = { id: participant.identity, name, page: body.page.slice(0, 80), gone: false };
  const n01 = (v) => Number.isFinite(v) && v >= 0 && v <= 1;
  if (Number.isFinite(body.x) && Number.isFinite(body.y)) return { ...base, x: Math.max(0, Math.min(100, body.x)), y: Math.max(0, Math.min(100, body.y)) };
  if (n01(body.u) && n01(body.v)) return { ...base, u: body.u, v: body.v, frame: typeof body.frame === "string" ? body.frame.slice(0, 80) : "" };
  if (Number.isInteger(body.row) && body.row >= 0 && body.row < 60 && n01(body.fx) && n01(body.fy)) return { ...base, row: body.row, fx: body.fx, fy: body.fy };
  return null;
}
// The page a pointer is on: an ink page, or a 360° or price scene by its id.
export function pointerPage(scene, view) {
  return pageKey(scene, view) || (scene && (scene.kind === "pano" || scene.kind === "price") ? scene.id : null);
}

// A page key as the spec has it, and only for a page this canvas has: a map ("location"), a plan's unit ("home:sunrise"),
// a board or a still (1–20), or the shared tab's glass ("share"). Anything else from the wire is refused (audit, P1).
export function pageOk(canvas, key) {
  if (typeof key !== "string" || key.length > 80) return false;
  if (key === "share") return true;
  if (isBoard(key)) return +key.split(":")[1] <= LIMITS.boards;
  if (isStill(key)) return +key.split(":")[1] <= LIMITS.stills;
  // Exactly a scene, or a scene and a unit — and only this canvas's own (no inherited names like "toString"), or a pen
  // holder could fill the page cap with pages no screen shows (re-audit N1).
  const parts = key.split(":");
  if (parts.length > 2) return false;
  const [id, variant] = parts;
  const own = (o, k) => Boolean(o) && Object.prototype.hasOwnProperty.call(o, k); // the newer static is not in Safari 15.0
  const sc = own(canvas?.byId, id) ? canvas.byId[id] : null;
  if (!sc) return false;
  if (sc.kind === "plan") return Boolean(variant && own(sc.variants, variant));
  return sc.kind === "fit" && variant === undefined;
}

// Reads a text stream up to a size, whatever its sender declared: a stream with no declared size is otherwise read whole
// (re-audit F4c). Returns null past the limit.
export async function readCapped(reader, max) {
  let out = "";
  for await (const chunk of reader) {
    out += chunk;
    if (out.length > max) return null;
  }
  return out;
}
